import React, { useReducer, useContext } from 'react';
import api from '../../utils/api';
import AuthContext from '../auth/authContext';
import DeckContext from './deckContext';
import deckReducer from './deckReducer';
import { searchPokemon } from '../../utils/pokemonService';
import { 
  getLocalDecks, 
  saveLocalDeck, 
  updateLocalDeck, 
  deleteLocalDeck 
} from '../../utils/localStorageUtils';
import { apiGet, apiPost, apiPut, apiDelete, handleApiOrLocal, loadDataWithFallback } from '../../utils/apiUtils';
import {
  GET_DECKS,
  GET_DECK,
  ADD_DECK,
  UPDATE_DECK,
  DELETE_DECK,
  DECK_ERROR,
  CLEAR_DECKS,
  SET_CURRENT_DECK,
  CLEAR_CURRENT_DECK,
  SEARCH_CARDS,
  CLEAR_SEARCH,
  CARD_ERROR,
  SET_ACTIVE_DECK,
  CLEAR_ACTIVE_DECK
} from '../types';

const DeckState = (props) => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  
  const initialState = {
    decks: [],
    currentDeck: null,
    searchResults: [],
    paginationInfo: {
      page: 1,
      pageSize: 25,
      totalCount: 0,
      totalPages: 1
    },
    error: null,
    loading: true,
    activeDeckId: null
  };

  const [state, dispatch] = useReducer(deckReducer, initialState);

  // Get User's Decks
  const getDecks = async () => {
    await loadDataWithFallback(
      () => apiGet('/api/decks'),
      getLocalDecks,
      (data) => dispatch({ type: GET_DECKS, payload: data }),
      !!(user && localStorage.getItem('token')),
      'decks'
    );
  };

  const getDeck = async (id) => {
    // Check if user is authenticated before making API call
    if (user && localStorage.getItem('token')) {
      try {
        const res = await api.get(`/api/decks/${id}`);
        dispatch({ type: GET_DECK, payload: res.data });
      } catch (err) {
        console.error('Error fetching deck:', err.message);
        dispatch({ type: DECK_ERROR, payload: err.response?.data?.message });
      }
    } else {
      // For local users, we could implement local deck lookup if needed
      console.log('User not authenticated, getDeck requires authentication');
      dispatch({ type: DECK_ERROR, payload: 'Authentication required to fetch individual deck' });
    }
  };

  const addDeck = async (deckData, forceAPI = false) => {
    const dataToSend = { ...deckData };
    if (user && user._id) {
      dataToSend.user = user._id;
    }
    
    const isOnline = !!(user && localStorage.getItem('token')) || forceAPI;
    
    const result = await handleApiOrLocal(
      () => apiPost('/api/decks', dataToSend),
      () => ({ success: true, data: saveLocalDeck(deckData) }),
      isOnline,
      'Failed to add deck'
    );
    
    if (result.success) {
      dispatch({ type: ADD_DECK, payload: result.data });
      
      // Automatically refresh decks after successful add
      setTimeout(() => {
        getDecks();
      }, 100);
      
      return result.data;
    }
    
    return null;
  };

  const updateDeck = async (id, deckData) => {
    try {
      // Check if it's a local deck
      if (id.startsWith('local_')) {
        const updatedDeck = updateLocalDeck(id, deckData);
        dispatch({ type: UPDATE_DECK, payload: updatedDeck });
        
        // Refresh decks after local update
        setTimeout(() => {
          getDecks();
        }, 100);
        
        return updatedDeck;
      } else {
        // Try to update via API
        const res = await api.put(`/api/decks/${id}`, deckData);
        dispatch({ type: UPDATE_DECK, payload: res.data });
        
        // Automatically refresh decks after successful update
        setTimeout(() => {
          getDecks();
          
          // Trigger analytics refresh if available
          if (typeof window !== 'undefined' && window.refreshTCGDeckAnalytics) {
            console.log('DeckState: Triggering TCG deck analytics refresh after deck update');
            window.refreshTCGDeckAnalytics();
          }
        }, 100);
        
        return res.data;
      }
    } catch (err) {
      console.error('Error updating deck:', err.message);
      dispatch({ type: DECK_ERROR, payload: err.response?.data?.message });
      throw err;
    }
  };

  const deleteDeck = async (id) => {
    try {
      // Check if it's a local deck
      if (id.startsWith('local_')) {
        deleteLocalDeck(id);
        dispatch({ type: DELETE_DECK, payload: id });
      } else {
        // Try to delete via API
        await api.delete(`/api/decks/${id}`);
        dispatch({ type: DELETE_DECK, payload: id });
      }
      
      // Automatically refresh decks after successful deletion
      setTimeout(() => {
        getDecks();
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshTCGDeckAnalytics) {
          console.log('DeckState: Triggering TCG deck analytics refresh after deck deletion');
          window.refreshTCGDeckAnalytics();
        }
      }, 100);
    } catch (err) {
      console.error('Error deleting deck:', err.message);
      dispatch({ type: DECK_ERROR, payload: err.response?.data?.message });
    }
  };

  const searchCards = async (queryStr, cardType = '', page = 1, pageSize = 25, filters = {}) => {
    if (localStorage.getItem('gameMode') === 'VGC') {
      const results = await searchPokemon(queryStr, pageSize);
      dispatch({ type: SEARCH_CARDS, payload: results });
      return results;
    }

    try {
      if (!queryStr || typeof queryStr !== 'string') {
        queryStr = 'name:*';
      }

      console.log('Final card search query (server):', queryStr, 'Page:', page);

      const res = await api.get('/api/cards/search', {
        params: { q: queryStr, page, pageSize }
      });

      const payload = res?.data || {};
      const cards = Array.isArray(payload.cards) ? payload.cards : [];
      const totalCount = payload.totalCount || cards.length;
      const totalPages = payload.totalPages || Math.ceil(totalCount / pageSize);

      dispatch({
        type: SEARCH_CARDS,
        payload: {
          cards,
          pagination: {
            page,
            pageSize,
            totalCount,
            totalPages
          }
        }
      });

      return cards;
    } catch (err) {
      console.error('Error searching cards:', err.message);
      dispatch({ type: CARD_ERROR, payload: err.response?.data?.message });
      return [];
    }
  };

  const clearSearch = () => dispatch({ type: CLEAR_SEARCH });
  const clearCurrentDeck = () => {
    dispatch({ type: CLEAR_CURRENT_DECK });
    return null;
  };
  const updateActiveDeckId = (deckId) => dispatch({ type: SET_ACTIVE_DECK, payload: deckId });
  const clearActiveDeckId = () => dispatch({ type: CLEAR_ACTIVE_DECK });

  // Copy a deck for reuse in the deck builder
  const copyDeck = (deck) => {
    // Set the current deck with the deck's data, but clear ID and name
    const deckCopy = {
      ...deck,
      _id: null,
      name: '',
      description: ''
    };

    dispatch({ type: SET_CURRENT_DECK, payload: deckCopy });
    return deckCopy;
  };

  return (
    <DeckContext.Provider
      value={{
        decks: state.decks,
        currentDeck: state.currentDeck,
        searchResults: state.searchResults,
        paginationInfo: state.paginationInfo,
        loading: state.loading,
        error: state.error,
        activeDeckId: state.activeDeckId,
        getDecks,
        getDeck,
        addDeck,
        updateDeck,
        deleteDeck,
        copyDeck,
        searchCards,
        clearSearch,
        clearCurrentDeck,
        updateActiveDeckId,
        clearActiveDeckId
      }}
    >
      {props.children}
    </DeckContext.Provider>
  );
};

export default DeckState;